import { AppConfig } from "../shared/utils/AppConfig.js";
import { ApiService } from "../shared/utils/ApiService.js";
import { askWithStream, collectChatHistory } from "../shared/utils/ChatStreamHelper.js";
import { TemplateEngine } from "../shared/utils/TemplateEngine.js";
import { Navbar } from "../shared/components/navbar/Navbar.js";
import { AuthService } from "../shared/services/AuthService.js";

import dashboardTemplate from "./templates/dashboard.html?raw";
import Swal from "sweetalert2";
import { HistorialService } from "../shared/utils/HistorialService.js";
import { ConsultasService } from "../shared/utils/ConsultasService.js";
import { TourService } from "../shared/utils/TourService.js";
import { escapeHtml } from "../shared/utils/sanitize.js";
import { getModeloIa } from "../shared/utils/ModeloIa.js";
import { subcategoriaDeArticulo, rutaArticuloHtml } from "../shared/utils/subcategorias.js";

// Devuelve el array de categorías permitidas para el usuario, o null si no hay restricción
function getCategoriasPermitidas(user) {
  if (!user) return null;
  if (user.rol === "admin") return null;
  return Array.isArray(user.categorias) && user.categorias.length > 0 ? user.categorias : null;
}

const TIPO_ICONOS = {
  texto: "fa-file-lines",
  pdf: "fa-file-pdf",
  video: "fa-circle-play",
  imagen: "fa-image",
};

export const DashboardController = {

  render: async() => {
    try {
      const user = AuthService.getCurrentUser();

      const categoriasPermitidas = getCategoriasPermitidas(user);
      const articulosPayload = categoriasPermitidas ? { categoriasPermitidas } : {};

      const [articulosResp, categoriasResp] = await Promise.all([
        ApiService.post("kb/articulos", articulosPayload),
        ApiService.get("categorias"),
      ]);

      const articulos = articulosResp?.data || [];
      const categorias = categoriasResp?.data || [];

      const catMap = Object.fromEntries(categorias.map(c => [c.id, c]));

      const totalArticulos = articulos.length;
      const totalCategorias = categorias.length;
      const topVotos = articulos.reduce((sum, a) => sum + (a.positivos || 0), 0);

      const recentArticulos = [...articulos]
        .slice(0, 6)
        .map(a => ({
          ...a,
          tipoIcono: TIPO_ICONOS[a.tipo] || "fa-file",
          rutaHtml: rutaArticuloHtml(
            catMap[a.categoriaId]?.nombre,
            AppConfig.features.subcategories ? subcategoriaDeArticulo(catMap[a.categoriaId], a) : ""
          ),
        }));

      const restrict = AppConfig.features.aiRestrictToCategories;
      const showAiChat = !restrict || user?.rol === "admin";

      const modeloIa = showAiChat ? await getModeloIa() : "";

      const historial = HistorialService.get().slice(0, 3);
      const historialHtml = historial.length
        ? historial.map(h => `
            <a href="#article?id=${h.id}" class="list-group-item list-group-item-action kb-article-row">
              <div class="d-flex align-items-center gap-3">
                <div class="kb-type-icon kb-type-${h.tipo}">
                  <i class="fas ${TIPO_ICONOS[h.tipo] || "fa-file"}"></i>
                </div>
                <div class="flex-grow-1 min-w-0">
                  <div class="fw-semibold text-truncate">${escapeHtml(h.titulo)}</div>
                  <small class="text-muted">${new Date(h.fechaVisita).toLocaleDateString("es-AR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</small>
                </div>
              </div>
            </a>`).join("")
        : "";

      const consultas = ConsultasService.get().slice(0, 4);
      const consultasHtml = consultas.length
        ? consultas.map(c => `
            <div class="list-group-item kb-article-row">
              <div class="d-flex align-items-center gap-3">
                <div class="kb-type-icon kb-type-texto">
                  <i class="fas fa-comment-dots"></i>
                </div>
                <div class="flex-grow-1 min-w-0">
                  <div class="fw-semibold text-truncate">${escapeHtml(c.texto)}</div>
                  <small class="text-muted">${new Date(c.fecha).toLocaleDateString("es-AR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</small>
                </div>
              </div>
            </div>`).join("")
        : "";

      // Tarjetas de actividad pre-renderizadas: van dentro de {{#if showAiChat}} y el
      // TemplateEngine no soporta {{#if}} anidados (rompía showAiChat y hasHistorial)
      const activityCard = (titulo, icono, clearId, itemsHtml) => `
        <div class="card flex-grow-1">
          <div class="card-header d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap-2">
              <i class="fas ${icono}"></i>
              <h5 class="mb-0">${titulo}</h5>
            </div>
            <button class="btn btn-outline-secondary btn-sm" id="${clearId}">
              <i class="fas fa-trash-alt me-1"></i>Limpiar
            </button>
          </div>
          <div class="card-body p-0">
            <div class="list-group list-group-flush">${itemsHtml}</div>
          </div>
        </div>`;

      const data = {
        historialCardHtml: historial.length
          ? activityCard("Vistos recientemente", "fa-history text-secondary", "kb-historial-clear", historialHtml)
          : "",
        consultasCardHtml: consultas.length
          ? activityCard("Últimas consultas", "fa-comments text-primary", "kb-consultas-clear", consultasHtml)
          : "",
        userName: user?.nombre || "Usuario",
        totalArticulos,
        totalCategorias,
        topVotos,
        recentArticulos,
        categorias,
        showAiChat,
        // Pre-renderizado: va dentro de {{#if showAiChat}} y el TemplateEngine no anida {{#if}}
        aiBadgeHtml: modeloIa
          ? `<span class="badge kb-badge-ai ms-auto" id="kb-ai-model">Powered by ${escapeHtml(modeloIa)}</span>`
          : "",
        historialHtml,
        hasHistorial: historial.length > 0,
        consultasHtml,
        hasConsultas: consultas.length > 0,
        hasHistorialOrConsultas: historial.length > 0 || consultas.length > 0,
      };

      const content = TemplateEngine.render(dashboardTemplate, data);
      document.getElementById("app").innerHTML = await Navbar.render() + content;

      DashboardController._bindChat();
      DashboardController._bindHistorial();
      DashboardController._bindStats();
      DashboardController._bindSuggest();
      TourService.resumeIfPending();
      TourService.maybeAutoStart(user?.rol === "admin");
    } catch (error) {
      console.error("Error loading dashboard:", error);
      document.getElementById("app").innerHTML =
        await Navbar.render() +
        `<div class="container mt-4"><div class="alert alert-danger"><i class="fas fa-exclamation-triangle me-2"></i>${error.message || "Error al cargar el dashboard"}</div></div>`;
    }
  },

  _bindStats: () => {
    document.getElementById("kb-stat-categorias")?.addEventListener("click", () => {
      document.getElementById("kb-categorias-section")?.scrollIntoView({ behavior: "smooth" });
    });

    document.getElementById("kb-stat-ia")?.addEventListener("click", () => {
      const input = document.getElementById("kb-chat-input");
      if (input) {
        input.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => input.focus(), 300);
      } else {
        window.location.hash = "knowledge";
      }
    });
  },

  _bindHistorial: () => {
    document.getElementById("kb-historial-clear")?.addEventListener("click", () => {
      HistorialService.clear();
      DashboardController.render();
    });
    document.getElementById("kb-consultas-clear")?.addEventListener("click", () => {
      ConsultasService.clear();
      DashboardController.render();
    });
  },

  _bindChat: () => {
    const input = document.getElementById("kb-chat-input");
    const btn = document.getElementById("kb-chat-btn");
    const msgs = document.getElementById("kb-chat-messages");
    const ask = async() => {
      const question = input?.value?.trim();
      if (!question) return;

      input.value = "";
      input.disabled = true;
      btn.disabled = true;

      const history = collectChatHistory(msgs);

      const userBubble = document.createElement("div");
      userBubble.className = "kb-chat-bubble user";
      userBubble.textContent = question;
      msgs.appendChild(userBubble);

      const loadingBubble = document.createElement("div");
      loadingBubble.className = "kb-chat-bubble assistant";
      loadingBubble.innerHTML = '<i class="fas fa-circle-notch fa-spin me-2 text-primary"></i>Consultando la base de conocimiento...';
      msgs.appendChild(loadingBubble);
      msgs.scrollTop = msgs.scrollHeight;

      try {
        const { fuentes } = await askWithStream({
          question,
          bubble: loadingBubble,
          msgs,
          history,
        });

        if (fuentes?.length) {
          ConsultasService.add(question);
        }

        if (!fuentes?.length) {
          const suggestEl = document.createElement("div");
          suggestEl.className = "text-end mt-1";
          suggestEl.innerHTML = '<button class="btn btn-link btn-sm text-muted p-0"><i class="fas fa-lightbulb me-1 text-warning small"></i><small>¿Sugerir este tema?</small></button>';
          suggestEl.querySelector("button").addEventListener("click", async() => {
            suggestEl.innerHTML = '<small class="text-muted"><i class="fas fa-check me-1 text-success"></i>Gracias, recibimos tu consulta.</small>';
            try { await ApiService.post("sugerencias", { texto: question, tipo: "ai" }); } catch {}
          });
          msgs.appendChild(suggestEl);
        }
      } catch {
        loadingBubble.innerHTML = '<i class="fas fa-exclamation-triangle me-2 text-danger"></i>Error al consultar la IA.';
      } finally {
        input.disabled = false;
        btn.disabled = false;
        input.focus();
        msgs.scrollTop = msgs.scrollHeight;
      }
    };

    btn?.addEventListener("click", ask);
    input?.addEventListener("keydown", (e) => { if (e.key === "Enter") ask(); });
  },

  _bindSuggest: () => {
    document.getElementById("kb-dash-suggest-btn")?.addEventListener("click", async() => {
      const { value: texto, isConfirmed } = await Swal.fire({
        title: "Sugerir un tema",
        input: "textarea",
        inputLabel: "¿Qué tema te gustaría que documentemos?",
        inputPlaceholder: "Ej: política de vacaciones, manual de ventas...",
        inputAttributes: { rows: 3 },
        showCancelButton: true,
        confirmButtonText: "Enviar sugerencia",
        cancelButtonText: "Cancelar",
        inputValidator: v => !v?.trim() ? "Ingresá el tema a sugerir" : null,
      });
      if (!isConfirmed || !texto?.trim()) return;
      try {
        await ApiService.post("sugerencias", { texto: texto.trim(), tipo: "manual" });
        Swal.fire({ icon: "success", title: "Sugerencia enviada", text: "Gracias, lo tendremos en cuenta.", timer: 2000, showConfirmButton: false });
      } catch {
        Swal.fire("Error", "No se pudo enviar la sugerencia", "error");
      }
    });
  },
};
